"use client";

import { ICurrentRankLeaderBoard } from "@/api";
import { Box, Typography, Button } from "@mui/material";
import { useDashboardContext } from "./Layout";
import { useEffect, useState } from "react";
import LeaderboardDialog, { ILeaderBoardExtend } from "./LeaderboardDialog";
import { useAlert } from "@/contexts/AlertContext";
import { Link } from "react-router";

export interface ICurrentRankLeaderBoardExtend
  extends ICurrentRankLeaderBoard {}

interface LeaderboardRankProps {
  initialData?: ICurrentRankLeaderBoardExtend;
}

export default function LeaderboardRank({ initialData }: LeaderboardRankProps) {
  const { getDataLeaderBoard } = useDashboardContext();
  const [leaderBoard, setLeaderBoard] = useState<ILeaderBoardExtend | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [currentRankLeaderBoard, setCurrentRankLeaderBoard] = useState<ICurrentRankLeaderBoardExtend | null>(null);
  const alert = useAlert();
  useEffect(() => {
    setCurrentRankLeaderBoard(initialData || null);
  }, [initialData]);

  if (
    !currentRankLeaderBoard ||
    !currentRankLeaderBoard.contest ||
    !currentRankLeaderBoard.contest.data ||
    currentRankLeaderBoard.contest.data.length === 0
  ) {
    return null;
  }

  const {
    current_rank,
    total_participant,
    contest,
    errorType,
    message,
    checkUserJoinAnyContest,
  } = currentRankLeaderBoard;

  const renderActionHtml = () => {
    if (
      checkUserJoinAnyContest &&
      checkUserJoinAnyContest.data &&
      checkUserJoinAnyContest.data.status === "1"
    ) {
      return (
        <Button
          onClick={handleShowLeaderBoardClick}
          variant="contained"
          data-testid="btnShowRankLeaderboard"
          sx={{
            textTransform: "none",
            bgcolor: "rgb(15, 105, 185)",
            "&:hover": {
              bgcolor: "rgb(13, 94, 166)",
            },
          }}
        >
          View Full Leaderboard
        </Button>
      );
    } else if (checkUserJoinAnyContest && checkUserJoinAnyContest.status) {
      return (
        <Box>
          <Typography sx={{ mt: 2 }}>
            {checkUserJoinAnyContest.status}
          </Typography>
          <Link
            to={`../my-contests`}
            className="mt-2 btnClickRedirectRegisterContest"
            style={{
              textDecoration: "none",
              color: "rgb(15, 105, 185)"
            }}
          >
            Click here &gt;&gt;&gt;
          </Link>
        </Box>
      );
    }
    return null;
  };
  
  const handleShowLeaderBoardClick = async () => {
    alert.showWait();
    try {
      const leaderBoard = await getDataLeaderBoard();
      setLeaderBoard(leaderBoard);
      setOpen(true);
    } catch (error) {
      console.error("Error fetching contests:", error);
    } finally {
      alert.hideWait();
    }
  };

  return (
    <>
      <LeaderboardDialog open={open} setOpen={setOpen} initialData={leaderBoard} />
      <Box
        sx={{
          p: 3,
          bgcolor: "white",
          borderRadius: 2,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          position: "relative",
          minHeight: 150,
          mt: 4,
          flex: 1,
          mr: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "rgb(15, 105, 185)",
            fontWeight: 500,
          }}
        >
          Your Leaderboard Rank
        </Typography>
        {contest && !contest.data ? (
          <Box sx={{ mt: 2 }}>
            {errorType === "school" && (
              <Box>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  It looks like you're not yet associated with a school.
                </Typography>
                <Typography>
                  - Contact your school administrator to get added.
                </Typography>
                <Typography>
                  - If you're not part of a school yet, explore our resources
                  and learn how to get started!
                </Typography>
              </Box>
            )}

            {errorType === "organization" && (
              <Box>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  It looks like your school is not yet associated with a contest
                  organization.
                </Typography>
                <Typography>
                  - Contact Pythaverse administrator to get added.
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
              mt: 2,
            }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  color: "rgb(255, 90, 31)",
                  fontWeight: 700,
                  mb: 1,
                }}
              >
                #{current_rank}
              </Typography>

              <Typography
                sx={{
                  color: "rgb(102, 102, 102)",
                  fontSize: "0.875rem",
                }}
              >
                out of {total_participant} participants
              </Typography>
            </Box>
            {renderActionHtml()}
          </Box>
        )}
      </Box>
    </>
  );
}
